import React, { useMemo } from 'react';
import useGameStore from '../store/gameStore';
import { useTranslation } from '../i18n';
import { computeBalanceSheet, getVehicleResaleValue } from '../utils/balanceSheet';
import { formatMoney } from '../utils/format';
import {
  Wallet, Landmark, Truck, Scale, AlertTriangle, TrendingUp, TrendingDown, Info,
  PiggyBank, FileSpreadsheet, ShieldAlert,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ReferenceLine } from 'recharts';
import MarkdownLite from '../components/MarkdownLite';

// Recharts config objects extracted outside the component so they are not
// re-created on every render (perf review fix 28/05/2026 v2).
const CHART_MARGIN = { top: 8, right: 16, left: -10, bottom: 4 };
const X_AXIS_TICK = { fill: '#94a3b8', fontSize: 11 };
const Y_AXIS_TICK = { fill: '#94a3b8', fontSize: 10 };
const TOOLTIP_CONTENT_STYLE = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 12,
};
const TOOLTIP_LABEL_STYLE = { color: '#cbd5e1', fontWeight: 700 };
const LEGEND_WRAPPER_STYLE = { fontSize: 11, paddingTop: 4 };
const Y_TICK_FORMATTER = (v) => `${Math.round(v / 1000)}k`;

/**
 * "Capitaux" — Simplified balance sheet view.
 * Pure read-only, derived from the live game state. No actions.
 */
export default function Capitaux() {
  const { t } = useTranslation();
  const {
    company, treasury, vehicles, credits, activePlacements, totalDays,
    tvaCollectedTotal, tvaDeductibleTotal, annualProfit, monthlyHistory,
    personalCapitalInjected, emergencyShareholderUsed,
    // 10/02/2026 (Lot N suite) — Retour user prod : « Les provisions TVA
    // ou IS n'apparaissent pas dans la vision des capitaux, ni la réserve
    // fiscale ». Les champs étaient bien calculés dans balanceSheet.js
    // mais omis dans cet appel useMemo → fallback à 0 → invisibles.
    tvaProvisionedBalance, isProvisionedBalance,
    fiscalReserveBalance, fiscalReserveInterestAccrued,
  } = useGameStore();

  const sheet = useMemo(() => computeBalanceSheet({
    treasury, vehicles, credits, activePlacements, totalDays,
    tvaCollectedTotal, tvaDeductibleTotal, annualProfit,
    tvaProvisionedBalance, isProvisionedBalance,
    fiscalReserveBalance, fiscalReserveInterestAccrued,
  }), [
    treasury, vehicles, credits, activePlacements, totalDays,
    tvaCollectedTotal, tvaDeductibleTotal, annualProfit,
    tvaProvisionedBalance, isProvisionedBalance,
    fiscalReserveBalance, fiscalReserveInterestAccrued,
  ]);

  const ownedDetail = useMemo(() => (vehicles || [])
    .filter(v => !v.isLeased)
    .map(v => {
      const resale = getVehicleResaleValue(v, totalDays);
      const mortgageLoan = v.mortgaged
        ? (credits || []).find(c => c.id === v.mortgageLoanId)
        : null;
      const debt = mortgageLoan ? Math.round(mortgageLoan.remainingPrincipal || 0) : 0;
      return { v, resale, debt, net: resale - debt, mortgageLoan };
    })
    .sort((a, b) => b.net - a.net),
  [vehicles, credits, totalDays]);

  const leasedDetail = useMemo(() => (vehicles || [])
    .filter(v => v.isLeased)
    .map(v => {
      const months = Math.max(0, Math.ceil(((v.leaseEndDay || 0) - totalDays) / 30));
      const monthly = v.leaseMonthlyPayment || 0;
      const residual = v.leaseResidualValue || Math.round((v.price || 0) * 0.25);
      return { v, months, monthly, residual, engagement: months * monthly + residual };
    }),
  [vehicles, totalDays]);

  // === Trend data for the equity chart (last 12 monthly closes) ===
  const trendData = useMemo(() => {
    const hist = (monthlyHistory || []).slice(-12);
    return hist.map(m => ({
      label: `${String(m.month).padStart(2, '0')}/${String(m.year).slice(-2)}`,
      equity:    m.equity ?? null,
      actif:     m.actifTotal ?? null,
      passif:    m.passifTotal ?? null,
      fleetNet:  m.fleetNet ?? null,
    }));
  }, [monthlyHistory]);

  return (
    <div className="space-y-4" data-testid="capitaux-page">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-accent/15">
          <FileSpreadsheet className="w-5 h-5 text-accent" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">{t('cap_title')}</h2>
          <p className="text-xs text-slate-500">{company?.name || 'SCD'} {t('cap_subtitle_at_day', { day: totalDays })}</p>
        </div>
      </div>

      {/* ===== Top summary row ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryTile
          testId="capitaux-actif"
          label={t('cap_total_actif')}
          value={formatMoney(sheet.actif.total)}
          icon={TrendingUp}
          color="money"
          hint={t('cap_actif_hint')}
        />
        <SummaryTile
          testId="capitaux-passif"
          label={t('cap_total_passif')}
          value={formatMoney(sheet.passif.total)}
          icon={TrendingDown}
          color="warn"
          hint={t('cap_passif_hint')}
        />
        <SummaryTile
          testId="capitaux-equity"
          label={t('cap_equity_label')}
          value={`${sheet.equity >= 0 ? '+' : ''}${formatMoney(sheet.equity)}`}
          icon={Scale}
          color={sheet.equity >= 0 ? 'money' : 'danger'}
          hint={t('cap_equity_hint', { solvency: sheet.solvencyPct, debt: sheet.debtRatioPct })}
        />
      </div>

      {/* ===== Personal capital tracker (06/02/2026 — P1 retour user) =====
        * Visible uniquement si une recapitalisation d'urgence a été utilisée
        * OU si l'apport personnel restant à rembourser est > 0. */}
      {(emergencyShareholderUsed || (personalCapitalInjected || 0) > 0) && (
        <div
          className="bg-ink-800 rounded-lg border border-amber-500/40 p-4"
          data-testid="personal-capital-tracker"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <PiggyBank className="w-5 h-5 text-amber-300" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-amber-300/80 font-bold">
                  Apport personnel (recapitalisation)
                </p>
                <p className="text-lg font-bold text-foreground">
                  Reste à rembourser : <span className="text-amber-300" data-testid="personal-capital-remaining">{formatMoney(personalCapitalInjected || 0)}</span>
                </p>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 max-w-md">
              Lors d&apos;une recapitalisation d&apos;urgence, vous injectez de l&apos;argent personnel
              dans l&apos;entreprise. Chaque dividende voté en Conseil annuel rembourse en
              priorité cet apport (jusqu&apos;à solde nul).
            </p>
          </div>
        </div>
      )}

      {/* ===== Alerts ===== */}
      {(sheet.solvencyPct < 20 || sheet.equity < 0 || sheet.debtRatioPct > 80) && (
        <div className="bg-ink-800 rounded-lg border border-danger/30 p-4" data-testid="capitaux-alerts">
          <h3 className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-danger" /> {t('cap_risk_signals')}
          </h3>
          <ul className="space-y-1 text-xs">
            {sheet.equity < 0 && (() => {
              // Soften the message at game start when the shortfall is just
              // the seed loan (15k) offsetting the starter truck — equity ≈ -800 €
              // which is not literally "cessation de paiements".
              const minor = sheet.actif.total > 0 && Math.abs(sheet.equity) / sheet.actif.total < 0.10;
              return (
                <li className={`flex items-center gap-2 ${minor ? 'text-warn' : 'text-danger'}`} data-testid="alert-equity-negative">
                  <span className={`w-1.5 h-1.5 rounded-full ${minor ? 'bg-warn' : 'bg-danger'}`} />
                  {minor
                    ? t('cap_alert_equity_minor')
                    : t('cap_alert_equity_critical')}
                </li>
              );
            })()}
            {sheet.solvencyPct < 20 && (
              <li className="flex items-center gap-2 text-warn" data-testid="alert-solvency-low">
                <span className="w-1.5 h-1.5 rounded-full bg-warn" />
                {t('cap_alert_solvency_low', { pct: sheet.solvencyPct })}
              </li>
            )}
            {sheet.debtRatioPct > 80 && (
              <li className="flex items-center gap-2 text-warn" data-testid="alert-debt-high">
                <span className="w-1.5 h-1.5 rounded-full bg-warn" />
                {t('cap_alert_debt_high', { pct: sheet.debtRatioPct })}
              </li>
            )}
          </ul>
        </div>
      )}

      {/* ===== Equity / Asset evolution chart ===== */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden" data-testid="equity-chart-panel">
        <div className="px-4 py-2.5 border-b border-ink-600 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-accent" />
          <h3 className="text-sm font-bold text-slate-200 tracking-wide uppercase font-mono">{t('cap_equity_chart_title')}</h3>
          <span className="ml-auto text-[10px] text-slate-500 font-mono">
            {trendData.length > 0 ? t('cap_chart_monthly_closes', { count: trendData.length }) : t('cap_chart_waiting')}
          </span>
        </div>
        {trendData.length < 2 ? (
          <div className="px-4 py-8 text-center" data-testid="equity-chart-empty">
            <Info className="w-5 h-5 text-slate-500 mx-auto mb-2" />
            <p className="text-xs text-slate-400">
              <MarkdownLite as="span" text={t('cap_chart_grows_text')} />
            </p>
            <p className="text-[10px] text-slate-500 mt-1">
              {t('cap_chart_day_info', { day: totalDays, next: 30 - (totalDays % 30) })}
            </p>
          </div>
        ) : (
          <div className="p-3 h-72" data-testid="equity-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={CHART_MARGIN}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke="#64748b" tick={X_AXIS_TICK} />
                <YAxis stroke="#64748b" tick={Y_AXIS_TICK} tickFormatter={Y_TICK_FORMATTER} />
                <Tooltip
                  contentStyle={TOOLTIP_CONTENT_STYLE}
                  labelStyle={TOOLTIP_LABEL_STYLE}
                  formatter={(value, name) => [formatMoney(value), name]}
                />
                <Legend wrapperStyle={LEGEND_WRAPPER_STYLE} />
                <ReferenceLine y={0} stroke="#475569" strokeDasharray="2 2" />
                <Line type="monotone" dataKey="equity"   name={t('cap_chart_equity_name')} stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="actif"    name={t('cap_chart_actif_name')}      stroke="#3B82F6" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
                <Line type="monotone" dataKey="passif"   name={t('cap_chart_passif_name')}     stroke="#F59E0B" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
                <Line type="monotone" dataKey="fleetNet" name={t('cap_chart_fleet_name')} stroke="#A855F7" strokeWidth={1.5} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ===== Two-column balance sheet ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* ACTIF */}
        <div className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden" data-testid="actif-panel">
          <div className="px-4 py-2.5 bg-money/10 border-b border-ink-600 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-money" />
            <h3 className="text-sm font-bold text-money tracking-wide uppercase font-mono">{t('cap_actif_panel')}</h3>
            <span className="ml-auto text-base font-black text-money font-mono">{formatMoney(sheet.actif.total)}</span>
          </div>
          <div className="divide-y divide-ink-600">
            <Row
              icon={Wallet}
              label={t('cap_row_treasury')}
              value={sheet.actif.cash}
              hint={t('cap_row_treasury_hint')}
              testId="actif-cash"
            />
            <Row
              icon={PiggyBank}
              label={t('cap_row_placements')}
              value={sheet.actif.placements}
              hint={t('cap_row_placements_hint', { count: (activePlacements || []).length })}
              testId="actif-placements"
            />
            {/* 17/06/2026 — Retour user : faire apparaître la provision sur
                la dette fiscale (TVA en attente, IS provisionné, placements
                fi en prévision de règlement) côté ACTIF. Le bilan les
                comptabilisait déjà mais elles n'étaient pas affichées. */}
            {sheet.actif.tvaProvisioned > 0 && (
              <Row
                icon={PiggyBank}
                label={t('cap_row_tva_provisioned')}
                value={sheet.actif.tvaProvisioned}
                hint={t('cap_row_tva_provisioned_hint')}
                testId="actif-tva-provisioned"
              />
            )}
            {sheet.actif.isProvisioned > 0 && (
              <Row
                icon={PiggyBank}
                label={t('cap_row_is_provisioned')}
                value={sheet.actif.isProvisioned}
                hint={t('cap_row_is_provisioned_hint')}
                testId="actif-is-provisioned"
              />
            )}
            {sheet.actif.fiscalReserveTotal > 0 && (
              <Row
                icon={PiggyBank}
                label={t('cap_row_fiscal_reserve')}
                value={sheet.actif.fiscalReserveTotal}
                hint={t('cap_row_fiscal_reserve_hint')}
                testId="actif-fiscal-reserve"
              />
            )}
          </div>
        </div>

        {/* PASSIF */}
        <div className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden" data-testid="passif-panel">
          <div className="px-4 py-2.5 bg-warn/10 border-b border-ink-600 flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-warn" />
            <h3 className="text-sm font-bold text-warn tracking-wide uppercase font-mono">{t('cap_passif_panel')}</h3>
            <span className="ml-auto text-base font-black text-warn font-mono">{formatMoney(sheet.passif.total)}</span>
          </div>
          <div className="divide-y divide-ink-600">
            <Row
              icon={Landmark}
              label={t('cap_row_credits')}
              value={sheet.passif.credits}
              hint={t('cap_row_credits_hint', { count: (credits || []).length })}
              testId="passif-credits"
            />
            <Row
              icon={Scale}
              label={t('cap_row_tax_debt')}
              value={sheet.passif.taxDebt}
              hint={t('cap_row_tax_hint', { tva: formatMoney(sheet.passif.tvaPending), is: formatMoney(sheet.passif.isProvision) })}
              testId="passif-tax"
            />
            {/* 17/06/2026 — Décomposition de la dette fiscale pour
                transparence pédagogique. Affichées seulement si > 0. */}
            {sheet.passif.tvaPending > 0 && (
              <Row
                icon={Scale}
                label={t('cap_row_tva_pending')}
                value={sheet.passif.tvaPending}
                hint={t('cap_row_tva_pending_hint')}
                testId="passif-tva-pending"
                indent
              />
            )}
            {sheet.passif.isProvision > 0 && (
              <Row
                icon={Scale}
                label={t('cap_row_is_provision_due')}
                value={sheet.passif.isProvision}
                hint={t('cap_row_is_provision_due_hint')}
                testId="passif-is-provision"
                indent
              />
            )}
          </div>
        </div>
      </div>

      {/* ===== Engagements leasing ===== */}
      {leasedDetail.length > 0 && (
        <div className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden" data-testid="lease-engagement-panel">
          <div className="px-4 py-2.5 border-b border-ink-600 flex items-center gap-2">
            <Truck className="w-4 h-4 text-purple-300" />
            <h3 className="text-sm font-bold text-slate-200 tracking-wide uppercase font-mono">{t('cap_lease_engagements_title')}</h3>
            <span className="ml-auto text-[10px] text-slate-500 font-mono">
              {t('cap_lease_total_remaining')} <span className="text-warn font-bold">{formatMoney(sheet.passif.leaseEngagement)}</span>
            </span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono border-b border-ink-600">
                <th className="text-left px-3 py-2">{t('cap_th_truck')}</th>
                <th className="text-center px-3 py-2">{t('cap_th_months_left')}</th>
                <th className="text-right px-3 py-2">{t('cap_th_monthly')}</th>
                <th className="text-right px-3 py-2">{t('cap_th_residual')}</th>
                <th className="text-right px-3 py-2">{t('cap_th_total_engagement')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-600">
              {leasedDetail.map(({ v, months, monthly, residual, engagement }) => (
                <tr key={v.id} className="hover:bg-ink-700/30 transition-colors">
                  <td className="px-3 py-2 font-semibold text-slate-200">{v.brand} {v.model}</td>
                  <td className="px-3 py-2 text-center text-slate-400">{months}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-200">{formatMoney(monthly)}/m</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-200">{formatMoney(residual)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-warn">{formatMoney(engagement)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== Pedagogical footer ===== */}
      <div className="bg-ink-800/50 rounded-lg border border-ink-600 p-3 text-[11px] text-slate-500 leading-relaxed" data-testid="capitaux-footer">
        <p className="flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-accent" />
          <span>
            <MarkdownLite text={t('cap_pedagogical_footer')} />
          </span>
        </p>
      </div>
    </div>
  );
}

// ===== Tiny presentational components =====

const SummaryTile = ({ testId, label, value, icon: Icon, color = 'slate', hint }) => {
  const colorMap = {
    money:  'text-money border-money/30 bg-money/5',
    danger: 'text-danger border-danger/30 bg-danger/5',
    warn:   'text-warn border-warn/30 bg-warn/5',
    slate:  'text-slate-300 border-ink-600 bg-ink-700/30',
  };
  return (
    <div
      className={`rounded-xl border p-3 ${colorMap[color] || colorMap.slate}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] uppercase tracking-wider opacity-80 font-mono">{label}</p>
      </div>
      <p className="text-lg font-black font-mono">{value}</p>
      {hint && <p className="text-[10px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
  );
};

const Row = ({ icon: Icon, label, value, hint, testId, indent = false }) => (
  <div className={`px-4 py-2.5 flex items-center gap-3 ${indent ? 'pl-10 bg-ink-700/20' : ''}`} data-testid={testId}>
    <Icon className={`w-4 h-4 flex-shrink-0 ${indent ? 'text-slate-600' : 'text-slate-500'}`} />
    <div className="flex-1 min-w-0">
      <p className={`text-xs font-semibold ${indent ? 'text-slate-300' : 'text-slate-200'}`}>{label}</p>
      {hint && <p className="text-[10px] text-slate-500">{hint}</p>}
    </div>
    <p className={`text-sm font-bold font-mono ${indent ? 'text-slate-300' : 'text-slate-100'}`} data-testid={`${testId}-value`}>
      {value >= 0 && '+'}
      {value === 0 ? '0 €' : value.toLocaleString('fr-FR') + ' €'}
    </p>
  </div>
);
